import { Component, inject, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from "@angular/core";
import { AsyncPipe, DecimalPipe, NgClass } from "@angular/common";
import { OverlayComponent } from "../../../../../components/overlay/overlay.component";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { BetPlacementService } from "../../../../../../../../shared/src/lib/services/bet-placement.service";
import { CoreService } from "../../../../../../../../shared/src/lib/services/core.service";
import { NavService } from "../../../../../../../../shared/src/lib/services/nav.service";
import { MatIconModule } from "@angular/material/icon";
import { TranslateModule } from "@ngx-translate/core";
import { MarketOpsService } from "../../market/market-ops.service";
import { Router } from "@angular/router";
import { PnlCalculatorService } from "../../../../../../../../shared/src/lib/services/pnl-calculator.service";
import { isNil } from "lodash";
import { MatButton } from "@angular/material/button";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "[app-f-market-row]",
  imports: [MatIconModule, NgClass, DecimalPipe, AsyncPipe, OverlayComponent, TranslateModule, MatButton],
  templateUrl: "./f-market-row.component.html",
  styleUrl: "./f-market-row.component.scss",
})
export class FMarketRowComponent implements OnInit, OnChanges, OnDestroy {
  @Input() market: any;
  @Input() metadata: any;
  @Input() data: any;
  @Input() update: any = null;
  @Input() runners: any = null;
  @Input() netEffect: Observable<any> | undefined;
  @Input() pNetEffect: Observable<any> | undefined;
  runnerNetEffect$ = new BehaviorSubject<any>(null);
  runnerPNetEffect$ = new BehaviorSubject<any>(null);
  readonly destroy$ = new Subject<void>();
  row: any = {
    no: {
      price: 0,
      spread: 0,
    },
    yes: {
      price: 0,
      spread: 0,
    },
  };
  placementService = inject(BetPlacementService);
  coreService = inject(CoreService);
  navService = inject(NavService);
  marketOps = inject(MarketOpsService);
  router = inject(Router);
  pnlCalculator = inject(PnlCalculatorService);
  priceChangeFlash: boolean = false;
  rowStatus: string = "";
  flashTimeouts: { [key: string]: any } = {};

  constructor() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes["update"]) {
      const update = changes["update"]?.currentValue;
      this.handleRowChanges(update);
      this.handleBetSizeChanges(update);
    }
  }

  ngOnInit() {
    this.initValues();
    this.setFlashingRule();
    this.getLineExposure();
  }

  handleBetSizeChanges(update: any) {
    if (this.market && update) {
      this.market["minBetSize"] = update?.minBetSize;
      this.market["maxBetSize"] = update?.maxBetSize;
    }
  }

  getLineExposure() {
    this.pnlCalculator
      .getLadderTable(this.market?.selectionId, "fancy", "exposure")
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: any) => {
        if (result?.type === "new") {
          this.runnerPNetEffect$.next(result?.value);
        } else {
          this.runnerPNetEffect$.next(null);
          this.runnerNetEffect$.next(result?.value);
        }
      });
  }

  setFlashingRule() {
    const slipSettings = this.placementService.getSlipSettings();
    this.priceChangeFlash = slipSettings?.betSlipSettings?.flashOnPriceChange === "enable";
  }

  initValues() {
    this.handleRowChanges(this.market);
  }

  handleRowChanges(updateData: any) {
    if (updateData) {
      this.rowStatus = updateData.status;
      let n = "no";
      let y = "yes";

      const newYesPrice = updateData?.priceYes;
      const newYesSpread = updateData?.spreadYes;
      const newNoPrice = updateData?.priceNo;
      const newNoSpread = updateData?.spreadNo;

      const yesChanged = this.row[y].price !== newYesPrice || this.row[y].spread !== newYesSpread;
      const noChanged = this.row[n].price !== newNoPrice || this.row[n].spread !== newNoSpread;

      if (yesChanged) {
        this.row[y].changed = yesChanged;
        this.row[y].price = newYesPrice;
        this.row[y].spread = newYesSpread;
        clearTimeout(this.flashTimeouts[y]);
        this.flashTimeouts[y] = setTimeout(() => {
          this.row[y].changed = false;
        }, 2000);
      }
      if (noChanged) {
        this.row[n].changed = noChanged;
        this.row[n].price = newNoPrice;
        this.row[n].spread = newNoSpread;
        clearTimeout(this.flashTimeouts[n]);
        this.flashTimeouts[n] = setTimeout(() => {
          this.row[n].changed = false;
        }, 2000);
      }
    }
  }

  handleSelect(_data: any) {
    if (_data?.PRICE === 0) return;
    this.navService.openBetSlipBar.next(true);
    const data = {
      ..._data,
      F_ODDS: _data?.SIZE / 100 + 1,
      PROVIDER: "SB",
      PROVIDER_ID: this.metadata?.providerId,
      SERVICE_ID: this.metadata?.serviceId,
      MARKET_ID: this.metadata?.marketId,
      SPORT_ID: this.metadata.eventType?.id,
      EVENT_ID: this.metadata?.eventId,
      PERSISTENCE_TYPE: this.placementService.persistenceType,
      marketName: "FANCY",
      eventName: this.metadata?.eventName,
      SELECTION_ID: this.market?.selectionId,
      HANDICAP: this.market?.handicap,
      runnerName: this.market?.runnerName,
      topic: this.metadata?.topic,
      minBet: this.market?.minBetSize,
      maxBet: this.market?.maxBetSize,
      descriptionPrefix: "Fancy",
      runners: this.runners,
      context: "fancy",
    };
    // console.log(data);
    // return;
    this.placementService.setParams({
      type: "LINE",
      data,
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected readonly isNil = isNil;
}
